import {useAtomValue, useSetAtom} from 'jotai';
import {useState} from 'react';
import {ScreenInfo} from '../components/ScreenInfo';
import {cleanupPartialEditSelection} from '@features/frame/partialEdit/usePartialEdit';
import {
  hasPartialSelectionAtomFor,
  hoveredElementAtom,
  isInPartialEditModeAtom,
} from '@features/frame/partialEdit/atoms';
import {useClearFrameSelection} from '@features/frame/partialEdit/useFrameSelection';
import {useSetActiveScreenId} from '@features/activeScreenIdContext/useSetActiveScreenId';
import amplitudeService from '@features/analytics/amplitudeService';
import {Frame} from '@features/frame/Frame';
import {LibraryItemFrame} from '@features/frame/LibraryItemFrame';
import {isComponentLibraryItem} from '@features/flow/utils/componentScreenId';
import type {NodeProps} from '@xyflow/react';
import type {FrameNodeType} from '../components/Canvas';
import {memo, useCallback, useContext} from 'react';
import {cn} from '@features/ui/utils';
import {FlowContext} from '@features/contexts/FlowContext';
import {streamingGenTypeAtom} from '@features/ai/streaming/streamingGenTypeAtom';
import {useIsScreenGenerating} from '@features/ai/streaming/useGeneration';
import {getEffectiveGenType} from '@features/server/utils/getEffectiveGenType';
import {
  CanvasExportToFigmaButton,
  CanvasExportToHtmlCss,
  CanvasDuplicateButton,
  CanvasShareButton,
} from '../components/canvas-export-buttons';
import {CanvaCreateVariantDropdown} from '../components/CanvasCreateVariantDropdown';
import {CanvasScreenTypeDropdown} from '../components/CanvasScreenTypeDropdown';
import {CanvasFeedbackButtons} from '../components/canvas-feedback-buttons';
import type {GenType} from '@features/flow/flowService';
import {FrameBorderHoverZone} from '../components/FrameBorderHoverZone';
import {useScreenCreationContext} from '../hooks/ScreenCreationContext';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {useIsActiveScreen} from '@features/activeScreenIdContext/useIsActiveScreen';
import {useMultipleNodesSelected} from '@features/activeScreenIdContext/useMultipleNodesSelected';
import {useSession} from '@features/auth/authClient';

// Stops toolbar clicks from bubbling to FrameNode's outer click handler,
// which would otherwise call setActiveScreenId on every action-button click and
// trigger a full canvas re-render storm (75-80ms commits in profiling).
function stopPropagation(event: React.SyntheticEvent) {
  event.stopPropagation();
}

const PreviewActionButtons = memo(function PreviewActionButtons({
  isActive,
  hasActiveStructure,
}: {
  isActive: boolean;
  hasActiveStructure: boolean;
}) {
  const {isAuthenticated} = useSession();
  if (!isAuthenticated) return null;
  if (!hasActiveStructure) return null;

  return (
    <div>
      {/* Invisible hover zone above the frame */}
      <div
        data-zoom-chrome='hover-zone'
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 'calc(-50px * var(--inverse-zoom, 1))',
          width: '100%',
          height: 'calc(50px * var(--inverse-zoom, 1))',
        }}
        className={cn(
          'z-[39] group-hover:pointer-events-auto',
          isActive ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      />
      {/* Toolbars container */}
      <div
        data-zoom-chrome='toolbar'
        style={{
          transform: `scale(var(--inverse-zoom, 1))`,
          transformOrigin: 'center top',
          top: 'calc(-50px * var(--inverse-zoom, 1))',
          left: 0,
          right: 0,
          position: 'absolute',
        }}
        className='pointer-events-none z-[42] flex justify-center gap-1'
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
      >
        <div
          className={cn(
            'shadow-ui-sm flex flex-row items-stretch rounded-xl bg-tooltip p-1 transition-[transform,opacity] duration-75 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100',
            isActive
              ? 'pointer-events-auto scale-100 opacity-100'
              : 'scale-95 opacity-0',
          )}
        >
          <CanvasExportToFigmaButton />
          <CanvasExportToHtmlCss />
        </div>
      </div>
    </div>
  );
});

const FrameActionButtons = memo(function FrameActionButtons({
  isActive,
  screenId,
  flowId,
  hasActiveStructure,
  genType,
}: {
  isActive: boolean;
  screenId: string;
  flowId: string;
  hasActiveStructure: boolean;
  genType: GenType;
}) {
  return (
    <div>
      {/* Invisible hover zone above the frame */}
      <div
        data-zoom-chrome='hover-zone'
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 'calc(-50px * var(--inverse-zoom, 1))',
          width: '100%',
          height: 'calc(50px * var(--inverse-zoom, 1))',
        }}
        className={cn(
          'z-[39] group-hover:pointer-events-auto',
          isActive ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      />
      {/* Toolbars container */}
      <div
        data-zoom-chrome='toolbar'
        style={{
          transform: `scale(var(--inverse-zoom, 1))`,
          transformOrigin: 'center top',
          top: 'calc(-50px * var(--inverse-zoom, 1))',
          left: 0,
          right: 0,
          position: 'absolute',
        }}
        className='pointer-events-none z-[42] flex justify-center gap-1'
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {/* Platform type */}
        <div
          className={cn(
            'shadow-ui-sm flex flex-row items-stretch rounded-xl bg-tooltip p-1 transition-[transform,opacity] duration-75 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100',
            isActive
              ? 'pointer-events-auto scale-100 opacity-100'
              : 'scale-95 opacity-0',
          )}
        >
          <CanvasScreenTypeDropdown
            screenId={screenId}
            genType={genType}
            flowId={flowId}
          />
        </div>
        {/* Variant + Duplicate */}
        <div
          className={cn(
            'shadow-ui-sm flex flex-row items-stretch rounded-xl bg-tooltip p-1 transition-[transform,opacity] duration-75 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100',
            isActive
              ? 'pointer-events-auto scale-100 opacity-100'
              : 'scale-95 opacity-0',
          )}
        >
          <CanvaCreateVariantDropdown screenId={screenId} />
          <CanvasDuplicateButton screenId={screenId} />
        </div>
        {/* Share and Export */}
        {hasActiveStructure && (
          <div
            className={cn(
              'shadow-ui-sm flex flex-row items-stretch rounded-xl bg-tooltip p-1 transition-[transform,opacity] duration-75 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100',
              isActive
                ? 'pointer-events-auto scale-100 opacity-100'
                : 'scale-95 opacity-0',
            )}
          >
            <CanvasShareButton screenId={screenId} />
            <CanvasExportToFigmaButton />
            <CanvasExportToHtmlCss />
          </div>
        )}
        {/* Feedback */}
        {hasActiveStructure && (
          <div
            className={cn(
              'shadow-ui-sm flex flex-row items-stretch rounded-xl bg-tooltip p-1 transition-[transform,opacity] duration-75 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100',
              isActive
                ? 'pointer-events-auto scale-100 opacity-100'
                : 'scale-95 opacity-0',
            )}
          >
            <CanvasFeedbackButtons
              screenId={screenId}
              flowId={flowId}
              variant='toolbar'
            />
          </div>
        )}
      </div>
    </div>
  );
});

export const FrameNode = memo(
  function FrameNode({
    data: {flowId, chatId, index, screenSize, hasActiveStructure},
    selected,
  }: NodeProps<FrameNodeType>) {
    const screenId = chatId;
    const {flow} = useContext(FlowContext);

    // Use per-screen atom to avoid re-rendering all FrameNodes when any screen's genType changes
    const streamingGenType = useAtomValue(streamingGenTypeAtom(screenId));
    const isGenerating = useIsScreenGenerating(screenId);

    // During generation, streaming genType takes priority (handles platform switch)
    // Otherwise, structure genType takes priority (stable state)
    const genType = getEffectiveGenType(
      isGenerating ? undefined : screenSize,
      streamingGenType,
      flow.genType,
    );

    const isActiveFrame = useIsActiveScreen(screenId);
    const setSelectedChatId = useSetActiveScreenId();
    const isInSelectMode = useAtomValue(isInPartialEditModeAtom);
    const hasPartialSelection = useAtomValue(
      hasPartialSelectionAtomFor(screenId),
    );
    const clearFrameSelection = useClearFrameSelection();
    const setHoveredElement = useSetAtom(hoveredElementAtom);
    const {createScreenRelativeTo} = useScreenCreationContext();
    const {readonly} = useContext(ReadonlyContext);
    const multipleNodesSelected = useMultipleNodesSelected();
    const [isScreenInfoHovered, setIsScreenInfoHovered] = useState(false);
    const [isFrameNodeHovered, setIsFrameNodeHovered] = useState(false);

    // Sticky-mount the action toolbar: once it's mounted (on first hover or
    // activation), keep it in the tree so its Radix dropdown subtree doesn't
    // unmount/remount as hover toggles. Hover toggles flip frequently during
    // zoom because frames slide under the cursor — without sticky mount, this
    // produces a Radix-portal mount storm (250+ fibers per zoom commit).
    const [hasMountedToolbar, setHasMountedToolbar] = useState(false);
    if (!hasMountedToolbar && (isActiveFrame || isFrameNodeHovered)) {
      setHasMountedToolbar(true);
    }

    const handleFrameClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        // Don't select screen when clicking banani buttons — generate-next
        // handles it. The event may reach us in one of two forms, so we
        // check both:
        //   • Forwarded shadow-DOM events (ShadowDivWithForwardEvents) —
        //     `event.nativeEvent.target` is the preserved shadow element.
        //   • Composed events that bubble naturally — target is retargeted
        //     to the shadow host, but `composedPath()` still contains the
        //     original shadow-DOM path.
        const nativeTarget = event.nativeEvent.target;
        const isBananiButton =
          (nativeTarget instanceof HTMLElement &&
            nativeTarget.closest('[data-media-type="banani-button"]') !==
              null) ||
          event.nativeEvent
            .composedPath()
            .some(
              (el) =>
                el instanceof HTMLElement &&
                el.dataset.mediaType === 'banani-button',
            );
        if (isBananiButton) {
          event.stopPropagation();
          return;
        }
        if (event.target instanceof HTMLElement && event.target.shadowRoot)
          return;

        // For modifier-key clicks, Canvas's onNodeClick handler is
        // responsible for promoting/toggling the multi-selection. Calling
        // setSelectedChatId here would collapse the selection to a single
        // id, so we skip it.
        if (event.metaKey || event.ctrlKey) return;
        setSelectedChatId(screenId);
      },
      [setSelectedChatId, screenId],
    );

    const handleScreenInfoClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        // Modifier-click on the screen-info bar: let Canvas's onNodeClick
        // handle the multi-select toggle so we don't collapse the selection.
        if (event.metaKey || event.ctrlKey) return;
        setSelectedChatId(screenId);
        cleanupPartialEditSelection();
        clearFrameSelection();
        setHoveredElement(null);
      },
      [setSelectedChatId, screenId, clearFrameSelection, setHoveredElement],
    );

    const handleCreateScreen = useCallback(
      (position: 'left' | 'right' | 'bottom') => {
        amplitudeService.logEvent('[Flow] add new screen from border', {
          position,
          chatId: screenId,
        });
        void createScreenRelativeTo(screenId, position);
      },
      [screenId, createScreenRelativeTo],
    );
    const isLibItem = isComponentLibraryItem(screenId);

    return (
      <div
        className='group relative'
        onMouseEnter={() => setIsFrameNodeHovered(true)}
        onMouseLeave={() => setIsFrameNodeHovered(false)}
      >
        <div
          className='absolute bottom-full left-0 right-0 flex justify-between'
          onMouseDown={handleScreenInfoClick}
          onMouseEnter={() => setIsScreenInfoHovered(true)}
          onMouseLeave={() => setIsScreenInfoHovered(false)}
        >
          <ScreenInfo
            currentChatId={screenId}
            arrId={index}
            isActiveFrame={isActiveFrame}
            isSelected={selected}
            genType={isLibItem ? 'components' : genType}
            isLibraryItem={isLibItem}
            hasPartialSelection={hasPartialSelection}
            isScreenInfoHovered={isScreenInfoHovered}
          />
        </div>
        <div id={screenId} className='relative' onClick={handleFrameClick}>
          {isLibItem ? (
            <LibraryItemFrame
              flowId={flowId}
              chatId={screenId}
              isActiveFrame={isActiveFrame}
              isSelected={selected}
            />
          ) : (
            <>
              {!multipleNodesSelected &&
                hasMountedToolbar &&
                (readonly ? (
                  <PreviewActionButtons
                    isActive={isActiveFrame}
                    hasActiveStructure={hasActiveStructure}
                  />
                ) : (
                  <FrameActionButtons
                    isActive={isActiveFrame}
                    screenId={screenId}
                    flowId={flowId}
                    hasActiveStructure={hasActiveStructure}
                    genType={genType}
                  />
                ))}
              <Frame
                flowId={flowId}
                chatId={screenId}
                isActiveFrame={isActiveFrame}
                isSelected={selected}
                genType={genType}
                hasPartialSelection={hasPartialSelection}
                isInSelectMode={isInSelectMode}
                isScreenInfoHovered={isScreenInfoHovered}
                isFrameNodeHovered={isFrameNodeHovered}
              />
              {!readonly && (
                <>
                  <FrameBorderHoverZone
                    position='left'
                    onCreateScreen={handleCreateScreen}
                  />
                  <FrameBorderHoverZone
                    position='right'
                    onCreateScreen={handleCreateScreen}
                  />
                  <FrameBorderHoverZone
                    position='bottom'
                    onCreateScreen={handleCreateScreen}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  },
  // Custom comparison: only re-render when data changes, ignore width/height/other ReactFlow props
  (prevProps, nextProps) =>
    prevProps.data.chatId === nextProps.data.chatId &&
    prevProps.data.flowId === nextProps.data.flowId &&
    prevProps.data.index === nextProps.data.index &&
    prevProps.data.screenSize === nextProps.data.screenSize &&
    prevProps.data.hasActiveStructure === nextProps.data.hasActiveStructure &&
    prevProps.selected === nextProps.selected,
);
